const RETRY_DELAY_MS = 500;

function abortError(signal) {
  return signal.reason ?? new DOMException("Aborted", "AbortError");
}

/** Resolves after `ms`, or rejects as soon as `signal` aborts. */
function wait(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(abortError(signal));
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      reject(abortError(signal));
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

/**
 * `fetch` that retries a 5xx response or a network failure after a short,
 * jittered pause. Aborts are never retried. The last response
 * is returned as is, so callers still check `res.ok`.
 * @param {string} url
 * @param {RequestInit} [opts]
 * @param {{ retries?: number, delayMs?: number }} [retry]
 * @returns {Promise<Response>}
 */
export async function fetchWithRetry(
  url,
  opts,
  { retries = 1, delayMs = RETRY_DELAY_MS } = {}
) {
  for (let attempt = 0; ; attempt++) {
    const isLastAttempt = attempt >= retries;
    try {
      const res = await fetch(url, opts);
      if (res.status < 500 || isLastAttempt) return res;
    } catch (error) {
      if (opts?.signal?.aborted || isLastAttempt) throw error;
    }
    await wait(delayMs * (1 + Math.random()), opts?.signal);
  }
}
